import { useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { useNotification } from '@/_shared/hooks';
import { deleteContentsLikesCall, postContentsLikesCall } from '../api';
import { ContentLike } from '../types';

export const useContentsLikes = () => {
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { execute: executePostContentsLikes } = postContentsLikesCall.hook();
  const { execute: executeDeleteContentsLikes } = deleteContentsLikesCall.hook();

  const insertContentLike = useCallback(
    (
      content: { contentType: ContentLike; metadata: { [key: string]: unknown } },
      onSuccessCallback?: (contentHash: string) => void,
      sendNotification = false,
    ) => {
      executePostContentsLikes({
        content: {
          contentType: content.contentType,
          metadata: content.metadata,
        },
        successSaga: ({ response }) => {
          if (onSuccessCallback) {
            onSuccessCallback(response.contentHash);
          }
          if (sendNotification) {
            notify(tShared('success.genericApiSuccess'), 'success');
          }
        },
        errorSaga: () => {
          if (sendNotification) {
            notify(tShared('errors.genericApiError'), 'error');
          }
        },
      });
    },
    [executePostContentsLikes, notify, tShared],
  );

  const removeContentLike = useCallback(
    (contentHash: string, onSuccessCallback?: () => void, sendNotification = false) => {
      executeDeleteContentsLikes({
        contentHash,
        successSaga: () => {
          if (onSuccessCallback) {
            onSuccessCallback();
          }
          if (sendNotification) {
            notify(tShared('success.genericApiSuccess'), 'success');
          }
        },
        errorSaga: () => {
          if (sendNotification) {
            notify(tShared('errors.genericApiError'), 'error');
          }
        },
      });
    },
    [executeDeleteContentsLikes, notify, tShared],
  );

  return {
    insertContentLike,
    removeContentLike,
  };
};
